import { useEffect, useRef } from "react";
import Cal, { getCalApi } from "@calcom/embed-react";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import ModalBasicLayout from "../../../components/Modal/ModalBasicLayout";
import {
  PRE_ACTIVATION_ONBOARDING_MODAL_ARIA_LABEL,
  PRE_ACTIVATION_ONBOARDING_MODAL_DESCRIPTION,
  PRE_ACTIVATION_ONBOARDING_MODAL_TITLE,
} from "../../../i18n/keysTranslations";
import { parseCalBookingEvent } from "../utils/parseCalBookingEvent";

const CAL_ONBOARDING_LINK = "team/customer-success-adle/onboarding-adle";
const CAL_ONBOARDING_NAMESPACE = "preActivationOnboarding";
const CAL_BOOKING_SUCCESS_ACTIONS = [
  "bookingSuccessful",
  "bookingSuccessfulV2",
];

const ModalPreActivationOnboarding = ({
  modalOpen = false,
  onCloseModal = () => {},
  prefill = {},
  onBookingConfirmed = () => {},
}) => {
  const { t } = useTranslation();
  const hasConfirmedBookingRef = useRef(false);
  const prefillName =
    typeof prefill.name === "string" ? prefill.name.trim() : "";
  const prefillEmail =
    typeof prefill.email === "string" ? prefill.email.trim() : "";
  const prefillPhone =
    typeof prefill.phone === "string" ? prefill.phone.trim() : "";
  const prefillWebsite =
    typeof prefill.website === "string" ? prefill.website.trim() : "";

  useEffect(() => {
    if (!modalOpen) {
      hasConfirmedBookingRef.current = false;
      return;
    }

    let isMounted = true;

    const onBookingSuccessful = (event) => {
      if (hasConfirmedBookingRef.current) {
        return;
      }

      const session = parseCalBookingEvent(event);
      if (!session) {
        return;
      }

      hasConfirmedBookingRef.current = true;
      onBookingConfirmed(session);
      onCloseModal();
    };

    const onInitCalUi = async () => {
      const cal = await getCalApi({ namespace: CAL_ONBOARDING_NAMESPACE });
      if (!isMounted) return;

      cal("ui", {
        theme: "light",
        hideEventTypeDetails: true,
        styles: {
          body: { background: "transparent" },
        },
        cssVarsPerTheme: {
          light: {
            "cal-bg": "#ffffff",
          },
        },
      });

      CAL_BOOKING_SUCCESS_ACTIONS.forEach((action) => {
        cal("on", {
          action,
          callback: onBookingSuccessful,
        });
      });
    };

    onInitCalUi();

    return () => {
      isMounted = false;
    };
  }, [modalOpen, onBookingConfirmed, onCloseModal]);

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth
      maxWidth="md"
      scroll="paper"
      showCloseButton
      ariaLabel={t(PRE_ACTIVATION_ONBOARDING_MODAL_ARIA_LABEL)}
      title={t(PRE_ACTIVATION_ONBOARDING_MODAL_TITLE)}
      description={t(PRE_ACTIVATION_ONBOARDING_MODAL_DESCRIPTION)}
      sxContent={{
        overflow: "hidden",
        pb: 3,
      }}
    >
      <Box
        sx={{
          overflow: "hidden",
          position: "relative",
          width: "100%",
          height: 600,
        }}
      >
        {modalOpen ? (
          <Cal
            key={CAL_ONBOARDING_LINK}
            namespace={CAL_ONBOARDING_NAMESPACE}
            calLink={CAL_ONBOARDING_LINK}
            style={{ width: "100%", height: "100%", overflow: "scroll" }}
            config={{
              layout: "month_view",
              theme: "light",
              ...(prefillName ? { name: prefillName } : {}),
              ...(prefillEmail ? { email: prefillEmail } : {}),
              ...(prefillPhone ? { attendeePhoneNumber: prefillPhone } : {}),
              ...(prefillWebsite
                ? { notes: prefillWebsite, website: prefillWebsite }
                : {}),
            }}
          />
        ) : null}
      </Box>
    </ModalBasicLayout>
  );
};

export default ModalPreActivationOnboarding;
